import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import analytics from '../../shared/services/analytics/analytics';
import { getSelfAlphasCorrelations, getAlphasCorrelationsPowerPool, getAlphasCorrelationsProd } from '../../actions/AlphasActions';
import {
  getSimulationCorrelations,
  getSimulationCorrelationsPowerPool,
  getSimulationCorrelationsProd,
} from '../../actions/Simulation/DataAction';
import { StateType } from '../../reducers';
import { alphasOptionsSettingsChildrenSelector, alphaMyAlphasResultsSelector } from '../../reducers/AlphasReducer/AlphasSelector';
import { hasProdPermission } from '../../reducers/AuthenticationReducer/AuthenticationSelector';
import { activeTabIndexSelector, getTabsSelector } from '../../reducers/EditorReducer/EditorSelector';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import { DispatchConnect } from '../../types';

import { AlphaCorrelationGeneric, AlphaCorrelationProps, AlphaCorrelationStateDispatchers, AlphaCorrelationStateProps } from './types';
import Correlation from './Correlation/Correlation';
import { isAlphaCreatedByTheCurrentUserSelector } from '../../reducers/GenericSelectors';

export class AlphaCorrelationContainer extends React.Component<AlphaCorrelationGeneric> {
  refreshCorrelationSelf = (e?: any) => {
    analytics.trackCustomEvent('sim_click_refresh_correlation');
    this.refreshCorrelationValue(this.props.dispatchGetSimulationCorrelations, this.props.dispatchGetAlphasCorrelationsSelf, e);
  };
  refreshCorrelationValue = (
    dispatchSimulationCorrelations: (alphaId: string, idParent: string, indexChild: any, isProdAlphas: boolean) => void,
    dispatchCorrelations: (alphaId: string, showErrors: boolean) => void,
    e?: any
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const alphaId = this.props.alpha.get('id');
    if (this.props.isSimulation) {
      const indexTab = this.props.currentTabActive;
      const tabId = this.props.editorTabs.getIn([indexTab, 'id']);
      const childIndex = this.props.editorTabs.getIn([indexTab, 'activeChildTab']);
      const idChild = this.props.editorTabs.getIn([indexTab, 'childTabs', childIndex, 'id']);
      dispatchSimulationCorrelations(alphaId, tabId, idChild !== undefined || idChild !== null ? idChild : null, this.props.isProdAlphas);
    } else {
      dispatchCorrelations(alphaId, this.props.isAlphaCreatedByTheCurrentUser);
    }
  };
  refreshCorrelationPowerPool = (e?: any) => {
    this.refreshCorrelationValue(
      this.props.dispatchGetSimulationCorrelationsPowerPool,
      this.props.dispatchGetAlphasCorrelationsPowerPool,
      e
    );
  };
  refreshCorrelationProd = (e?: any) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const alphaId = this.props.alpha && this.props.alpha.get('id');
    if (this.props.isSimulation) {
      const indexTab = this.props.currentTabActive;
      const tabId = this.props.editorTabs.getIn([indexTab, 'id']);
      const childIndex = this.props.editorTabs.getIn([indexTab, 'activeChildTab']);
      const idChild = this.props.editorTabs.getIn([indexTab, 'childTabs', childIndex, 'id']);
      this.props.dispatchGetSimulationCorrelationsProd(alphaId, tabId, idChild !== undefined || idChild !== null ? idChild : null);
    } else {
      this.props.dispatchGetAlphasCorrelationsProd(alphaId);
    }
  };

  render() {
    const { alpha, isProdAlphas, id, settingsOptions } = this.props;
    if (alpha && settingsOptions) {
      return (
        <Correlation
          alfaId={alpha.get('id')}
          alpha={alpha}
          isProdAlphas={isProdAlphas}
          refreshCorrelationSelf={this.refreshCorrelationSelf}
          refreshCorrelationProd={this.refreshCorrelationProd}
          refreshCorrelationPowerPool={this.refreshCorrelationPowerPool}
          fetchingCorrelationSelf={alpha.get('fetchingCorrelation')}
          fetchingCorrelationProd={alpha.get('fetchingCorrelationProd')}
          fetchingCorrelationPowerPool={alpha.get('fetchingCorrelationPowerPool')}
          id={id}
          settingsOptions={settingsOptions}
        />
      );
    } else {
      return (
        <div id={id} className="alphas-details-content__box">
          <div className="correlation__title">
            {' '}
            <FormattedMessageHelper id="correlation.1" />{' '}
          </div>
          <div className="correlation__no-results">
            {' '}
            <FormattedMessageHelper id="correlation.no.options" />
          </div>
        </div>
      );
    }
  }
}

function mapStateToProps(state: StateType, props: AlphaCorrelationProps): AlphaCorrelationStateProps {
  const alphaPath = props.path.concat(['alphaDetail']);

  return {
    alpha: state.getIn(alphaPath),
    isProdAlphas: hasProdPermission(state),
    editorTabs: getTabsSelector(state),
    myAlphasResults: alphaMyAlphasResultsSelector(state),
    currentTabActive: activeTabIndexSelector(state),
    settingsOptions: alphasOptionsSettingsChildrenSelector(state),
    isAlphaCreatedByTheCurrentUser: isAlphaCreatedByTheCurrentUserSelector(state),
  };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): AlphaCorrelationStateDispatchers => ({
  dispatchGetAlphasCorrelationsProd: async (alphaId: string) => dispatch(getAlphasCorrelationsProd(alphaId)),
  dispatchGetAlphasCorrelationsSelf: async (alphaId: string, showError?: boolean) =>
    dispatch(getSelfAlphasCorrelations(alphaId, showError)),
  dispatchGetAlphasCorrelationsPowerPool: async (alphaId: string, showError?: boolean) =>
    dispatch(getAlphasCorrelationsPowerPool(alphaId, showError)),
  dispatchGetSimulationCorrelationsProd: async (alphaId: string, idParent: string, idChild: string | null) =>
    dispatch(getSimulationCorrelationsProd(alphaId, idParent, idChild)),
  dispatchGetSimulationCorrelationsPowerPool: async (alphaId: string, idParent: string, idChild: string | null, isProdAlphas: boolean) =>
    dispatch(getSimulationCorrelationsPowerPool(alphaId, idParent, idChild, isProdAlphas)),
  dispatchGetSimulationCorrelations: async (alphaId: string, idParent: string, idChild: string | null, isProdAlphas: boolean) =>
    dispatch(getSimulationCorrelations(alphaId, idParent, idChild, isProdAlphas)),
});

// `forwardRef: true` lets AlphaCorrelation.test.tsx reach the wrapped AlphaCorrelationContainer
// instance via a ref (the RTL replacement for enzyme's `.instance()`); it's a no-op in production.
export default connect<AlphaCorrelationStateProps, DispatchConnect<AlphaCorrelationStateDispatchers>, any, StateType>(
  mapStateToProps,
  mapDispatchToProps,
  undefined,
  { forwardRef: true }
)(AlphaCorrelationContainer);
